import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
// Utils
import { showToast } from '../utils/ToastsUtil';
import ActionsUtil from '../utils/ActionsUtil';
import i18n from '../locales/i18n';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class ActionsService {
    static addActionOccurrences({ projectId, actionId, date, isUrgent, customFields, elements }, successToast = 'action_added', errorToast = 'action_addition_failed') {
        return Axios.post(BACKEND_ENDPOINT + 'actions/occurrences', { projectId, actionId, date, isUrgent, customFields, elements }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (navigator.onLine) showToast(errorToast);
            return;
        });
    }

    static updateActionOccurrences(projectId, occurrences, { deathCauseId = null, cancelPendingRecurrences = false, successToast = 'action_updated', errorToast = 'action_update_failed' } = {}) {
        return Axios.put(`${BACKEND_ENDPOINT}actions/occurrences?projectId=${projectId}&deathCauseId=${deathCauseId || 0}&cancelPendingRecurrences=${cancelPendingRecurrences ? 'true' : 'false'}`, occurrences, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (navigator.onLine) showToast(errorToast);
            return;
        });
    }

    static deleteActionOccurrences(projectId, ids, successToast = 'action_deleted', errorToast = 'action_deletion_failed') {
        return Axios.delete(`${BACKEND_ENDPOINT}actions/occurrences?projectId=${projectId}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (navigator.onLine) showToast(errorToast);
            return;
        });
    }

    static addActionSeries({ projectId, actionId, startDate, endDate, recurrence, isUrgent, customFields, elements }) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/series', { projectId, actionId, startDate, endDate, recurrence, isUrgent, customFields, elements }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_added');
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('action_addition_failed');
            return;
        });
    }

    static updateActionSeries({ id, projectId, endDate, isUrgent }) {
        return Axios.put(BACKEND_ENDPOINT + 'actions/series', { id, projectId, endDate, isUrgent }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_updated');
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('action_update_failed');
            return;
        });
    }

    static addActionSeriesElements(projectId, seriesId, elements, successToast = 'action_linked_single', errorToast = 'action_link_failed') {
        return Axios.post(BACKEND_ENDPOINT + 'actions/series/elements', { seriesId, projectId, elements }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (navigator.onLine) showToast(errorToast);
            return;
        });
    }

    static deleteActionSeriesElements(projectId, seriesId, elementIds, successToast = 'action_unlinked', errorToast = 'action_unlink_failed') {
        return Axios.delete(BACKEND_ENDPOINT + 'actions/series/elements', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { seriesId, projectId, elementIds }
        }).then(response => {
            if (successToast) showToast(successToast);
            return response.data;
        }, () => {
            if (navigator.onLine) showToast(errorToast);
            return;
        });
    }

    static deleteActionSeries(projectId, id) {
        return Axios.delete(`${BACKEND_ENDPOINT}actions/series?projectId=${projectId}&id=${id}`, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_deleted');
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('action_deletion_failed');
            return;
        });
    }

    // Opérations sur un groupe de l'adaptateur (série, ou action ponctuelle un jour donné)
    static addElementsToGroup(projectId, group, elements, successToast = 'action_linked_single', errorToast = 'action_link_failed') {
        return group.seriesId
            ? ActionsService.addActionSeriesElements(projectId, group.seriesId, elements, successToast, errorToast)
            : ActionsService.addActionOccurrences({ projectId, actionId: group.actionId, date: group.startDate, isUrgent: group.isUrgent, customFields: null, elements }, successToast, errorToast);
    }

    // Modification d'une série : date de fin et urgence (les occurrences ponctuelles passent par updateActionOccurrences)
    static updateGroup(projectId, group, { endDate, isUrgent }) {
        return ActionsService.updateActionSeries({ id: group.seriesId, projectId, endDate, isUrgent })
            .then(response => response && { changes: { created: response.created, updated: response.updated, removedIds: response.removedIds }, seriesChanges: { updated: [response.series] }, histories: [] });
    }

    static removeElementsFromGroup(projectId, group, elementIds, successToast = 'action_unlinked', errorToast = 'action_unlink_failed') {
        if (group.seriesId) return ActionsService.deleteActionSeriesElements(projectId, group.seriesId, elementIds, successToast, errorToast);
        const ids = group.occurrences.filter(occurrence => elementIds.includes(occurrence.elementId) && !occurrence.status).map(occurrence => occurrence.id);
        return ActionsService.deleteActionOccurrences(projectId, ids, successToast, errorToast);
    }

    static getActionOccurrences(projectId) {
        return Axios.get(BACKEND_ENDPOINT + 'actions/occurrences/' + projectId, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('action_loading_failed');
            return;
        });
    }

    static getActions() {
        return Axios.get(BACKEND_ENDPOINT + 'actions/', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('action_loading_failed');
            return;
        });
    }

    static exportActionAsPDF(group, actionLabel, image, addPrices) {
        const occurrenceIds = group.occurrences.map(occurrence => occurrence.id);
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportPDF', { occurrenceIds, image, recurrenceInfos: ActionsUtil.getRecurrencesInfos(group), addPrices }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            const filename = 'Fiche action_' + actionLabel + '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', 'action', `"${actionLabel}"`);
        }, () => {
            if (navigator.onLine) showToast('action_pdf_export_failed');
        });
    }

    static exportBacklogAsPDF(data, toastCustomTexts) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportPDF/backlog/', data, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            const filename = data.groupedBy
                ? `Fiche actions_${data.startDate}_${data.endDate}.pdf`.replaceAll('/', '-')
                : `${data.reference}_Actions.pdf`;
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('backlog_pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (navigator.onLine) showToast('backlog_pdf_export_failed', ...toastCustomTexts);
        });
    }

    static exportBacklogAsXLSX(baseProject, rows, groupedBy, customFields, columnsToRender) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportXLSX/backlog/', { baseProjectId: baseProject.id, rows, groupedBy, customFields, columnsToRender }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${baseProject.label} - ${i18n.t("Actions")}.xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (navigator.onLine) showToast('elements_xlsx_export_failed');
        });
    }

    static exportAnnualBudgetAsXLSX(baseProject, startDate, endDate, priceListId) {
        return Axios.post(BACKEND_ENDPOINT + 'actions/exportXLSX/annualBudget/', { baseProjectId: baseProject.id, startDate, endDate, priceListId }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${baseProject.label} - ${i18n.t("Actions")} (${i18n.t("Budget annuel")}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
        }, () => {
            if (navigator.onLine) showToast('elements_xlsx_export_failed');
        });
    }

    static addAction(action) {
        return Axios.post(BACKEND_ENDPOINT + 'admin/actions/', action, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('action_added');
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('action_addition_failed');
            return;
        });
    }

    static updateActions(actions) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/actions/', actions, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('actions_updated');
            return response.status;
        }, () => {
            if (navigator.onLine) showToast('actions_update_failed');
            return;
        });
    }

    static deleteActions(ids) {
        return Axios.delete(BACKEND_ENDPOINT + 'admin/actions/', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids: ids }
        }).then(response => {
            return response.status;
        }, () => {
            showToast(ids.length > 1 ? 'actions_deletion_failed' : 'action_deletion_failed');
            return;
        });
    }

    static getActionHistory(elementId) {
        return Axios.get(BACKEND_ENDPOINT + 'actions/history/?elementId=' + elementId, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) if (navigator.onLine) showToast('element_history_loading_failed');
            return;
        });
    }
}